/**
 * Debug session management
 * Handles tracking and cleanup of console.log statements during debug sessions
 */

import type { DebugSession, DebugSessionManager } from './types';

export class DebugSessionManagerImpl implements DebugSessionManager {
  private sessions: Map<string, DebugSession> = new Map();
  private originalConsoleLog: typeof console.log;
  private isIntercepting = false;

  constructor() {
    this.originalConsoleLog = console.log;
  }

  /**
   * Start a new debug session
   */
  start(sessionId: string): void {
    if (this.sessions.has(sessionId)) {
      console.warn(`Debug session '${sessionId}' already active`);
      return;
    }

    this.sessions.set(sessionId, {
      id: sessionId,
      startTime: Date.now(),
      consoleLogs: [],
    });

    // Start intercepting console.log if not already doing so
    if (!this.isIntercepting && this.sessions.size === 1) {
      this.startIntercepting();
    }

    this.originalConsoleLog(`🐛 Debug session started: ${sessionId}`);
  }

  /**
   * End a debug session and optionally clean up console.logs
   */
  end(sessionId: string, cleanup = true): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn(`Debug session '${sessionId}' not found`);
      return;
    }

    const duration = Date.now() - session.startTime;
    const logCount = session.consoleLogs.length;

    if (cleanup && typeof console.clear === 'function') {
      // Note: console.clear() doesn't work in all environments
      // In production, we might want to just suppress the logs instead
      this.originalConsoleLog(
        `🧹 Debug session '${sessionId}' ended: ${logCount} console.logs tracked (${duration}ms)`
      );
    } else {
      this.originalConsoleLog(
        `🐛 Debug session '${sessionId}' ended: ${logCount} console.logs kept (${duration}ms)`
      );
    }

    this.sessions.delete(sessionId);

    // Stop intercepting if no more sessions
    if (this.sessions.size === 0 && this.isIntercepting) {
      this.stopIntercepting();
    }
  }

  /**
   * Add a trace message to a debug session
   */
  trace(sessionId: string, message: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn(`Debug session '${sessionId}' not found`);
      return;
    }

    // Add as a tracked console.log
    session.consoleLogs.push({
      args: [`[${sessionId}] ${message}`],
      stack: new Error().stack,
    });

    // Also output it
    this.originalConsoleLog(`[${sessionId}] ${message}`);
  }

  /**
   * Check if any debug sessions are active
   */
  isActive(): boolean {
    return this.sessions.size > 0;
  }

  /**
   * Get list of active session IDs
   */
  getActiveSessions(): string[] {
    return Array.from(this.sessions.keys());
  }

  /**
   * Start intercepting console.log calls
   */
  private startIntercepting(): void {
    if (this.isIntercepting) return;

    console.log = (...args: unknown[]) => {
      // Track the log in all active sessions
      this.sessions.forEach((session) => {
        session.consoleLogs.push({
          args,
          stack: new Error().stack,
        });
      });

      // Still output to console
      this.originalConsoleLog(...args);
    };

    this.isIntercepting = true;
  }

  /**
   * Stop intercepting console.log calls
   */
  private stopIntercepting(): void {
    if (!this.isIntercepting) return;

    console.log = this.originalConsoleLog;
    this.isIntercepting = false;
  }

  /**
   * Get debug session statistics
   */
  getSessionStats(sessionId: string): { duration: number; logCount: number } | null {
    const session = this.sessions.get(sessionId);
    if (!session) return null;

    return {
      duration: Date.now() - session.startTime,
      logCount: session.consoleLogs.length,
    };
  }

  /**
   * Clear all sessions (emergency cleanup)
   */
  clearAll(): void {
    const sessionCount = this.sessions.size;
    this.sessions.clear();

    if (this.isIntercepting) {
      this.stopIntercepting();
    }

    if (sessionCount > 0) {
      this.originalConsoleLog(`🧹 Cleared ${sessionCount} debug sessions`);
    }
  }
}
